import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const DOC_TYPES = ['employment_contract', 'nda', 'id_card', 'residence', 'iban', 'sgk_form', 'emergency_contact', 'vehicle', 'private_health', 'custom'];
export const STATUSES = ['pending', 'uploaded', 'approved', 'rejected', 'waived', 'cancelled'];
export const KVKK_CATS = ['employment_records', 'health', 'communication', 'financial'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const usePbDocumentsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/preboarding-documents?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const approve = useCallback(async (id) => {
    try { await api.post(`/v1/preboarding-documents/${id}/approve`, {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const reject = useCallback(async (id, reason) => {
    try { await api.post(`/v1/preboarding-documents/${id}/reject`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const waive = useCallback(async (id, reason) => {
    try { await api.post(`/v1/preboarding-documents/${id}/waive`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try { await api.delete(`/v1/preboarding-documents/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  // Form value ↔ PbDocumentRequirementRequest. Only the create/update metadata
  // fields are sent; file upload happens through the separate /upload endpoint.
  const toPayload = useCallback((formData) => ({
    preboardingId: formData.preboardingId || null,
    documentType: formData.documentType || 'custom',
    title: formData.title || '',
    isMandatory: !!formData.isMandatory,
    requiresHrApproval: !!formData.requiresHrApproval,
    requiresESignature: !!formData.requiresESignature,
    kvkkCategory: formData.kvkkCategory || 'employment_records',
  }), []);

  const toFormValues = useCallback((row) => ({
    id: row.id,
    preboardingId: row.preboardingId || null,
    documentType: row.documentType || 'custom',
    title: row.title || '',
    isMandatory: !!row.isMandatory,
    requiresHrApproval: !!row.requiresHrApproval,
    requiresESignature: !!row.requiresESignature,
    kvkkCategory: row.kvkkCategory || 'employment_records',
  }), []);

  const create = useCallback(async (formData) => {
    try { await api.post('/v1/preboarding-documents', toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const update = useCallback(async (id, formData) => {
    try { await api.put(`/v1/preboarding-documents/${id}`, toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const columns = useMemo(() => {
    const docTypeLabel = (s) => t(`preboardingDoc.type.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`preboardingDoc.status.${s}`, { defaultValue: s });
    const kvkkLabel = (s) => t(`preboardingDoc.kvkk.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'preboardingId', label: 'PB', options: { filter: false, sort: false, display: 'excluded' } },
      {
        // Müşteri şikayeti: liste kimin belgesi olduğunu göstermiyor VE aranamıyordu.
        // `personFullName[like]` Task 8'de backend'e eklendi (PbDocumentRequirementFilterRequest).
        name: 'personFullName', label: t('preboardingDoc.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ), filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
      },
      {
        name: 'title', label: t('preboardingDoc.title'),
        options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
      },
      {
        name: 'documentType', label: t('preboardingDoc.typeLabel'),
        options: {
          ...multiSelectFilter(t('preboardingDoc.typeLabel'), DOC_TYPES, docTypeLabel),
          customBodyRender: (v) => docTypeLabel(v),
        },
      },
      {
        name: 'status', label: t('preboardingDoc.statusLabel'),
        options: {
          ...multiSelectFilter(t('preboardingDoc.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      {
        name: 'kvkkCategory', label: t('preboardingDoc.kvkkLabel'),
        options: {
          ...multiSelectFilter(t('preboardingDoc.kvkkLabel'), KVKK_CATS, kvkkLabel),
          customBodyRender: (v) => kvkkLabel(v),
        },
      },
      {
        name: 'isMandatory', label: t('preboardingDoc.isMandatory'),
        options: { filter: true, sort: true, customBodyRender: (v) => v ? t('common.yes') : t('common.no') },
      },
      {
        name: 'requiresESignature', label: t('preboardingDoc.requiresESignature'),
        options: { filter: true, sort: true, customBodyRender: (v) => v ? t('common.yes') : t('common.no') },
      },
      dateFilterColumn('uploadedAt', t('preboardingDoc.uploadedAt'), lang),
    ];
  }, [t, lang, data]);

  const objectSchema = useMemo(() => {
    const docTypeLabel = (s) => t(`preboardingDoc.type.${s}`, { defaultValue: s });
    const kvkkLabel = (s) => t(`preboardingDoc.kvkk.${s}`, { defaultValue: s });

    return [
      {
        name: 'preboardingId', label: t('preboardingDoc.preboarding'), type: 'asyncSelect', required: true,
        // İŞE HAZIRLIK (PB) süreci — `/v1/onboardings` DEĞİL. O uç İşe Alıştırma (OB)
        // modülünün listesidir; oradan seçilen kimlik bir ObOnboarding.Id'dir ve backend
        // onu PbPreboardings'te aradığı için kayıt her denemede 404 `preboarding.notFound`
        // ile düşüyordu. Sözleşme kardeş ekranla (preboarding-tasks/config) aynıdır.
        onSearch: async (q) => {
          const params = { 'status[in]': 'active,extended', pageSize: 20 };
          if (q) params['personName[like]'] = q;
          const res = await api.get('/v1/preboardings', { params });
          const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
          return items.map((p) => ({ id: p.id, name: p.personFullName || p.id }));
        },
        onGetById: async (id) => {
          const res = await api.get(`/v1/preboardings/${id}`);
          const p = res.data?.data || {};
          return { id, name: p.personFullName || id };
        },
      },
      { name: 'title', label: t('preboardingDoc.title'), type: 'text', required: true },
      {
        name: 'documentType', label: t('preboardingDoc.typeLabel'), type: 'select', grid: 6,
        options: DOC_TYPES.map((v) => ({ id: v, name: docTypeLabel(v) })),
      },
      {
        name: 'kvkkCategory', label: t('preboardingDoc.kvkkLabel'), type: 'select', grid: 6,
        options: KVKK_CATS.map((v) => ({ id: v, name: kvkkLabel(v) })),
      },
      { name: 'isMandatory', label: t('preboardingDoc.isMandatory'), type: 'checkbox' },
      { name: 'requiresHrApproval', label: t('preboardingDoc.requiresHrApproval'), type: 'checkbox' },
      { name: 'requiresESignature', label: t('preboardingDoc.requiresESignature'), type: 'checkbox' },
    ];
  }, [t]);

  return { fetching, data, pagination, columns, objectSchema, toFormValues, refresh, create, update, approve, reject, waive, remove };
};

export default usePbDocumentsApi;
